import { Alert, Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// Components
import { Form, Input, Select, Slider, ToggleSwitch } from 'Components';
import { MILES_TO_KPH } from "../../../../../../../../core/data/Helpers";

//JBus
export const HeavyDutyDTC = (props) => {
  const { value, onChange, formRef, currentAlert, setCurrentAlert } = props
  const { messages } = useIntl()
  const isKph = useSelector((state) => state.securityRedux.user.isKph)
  const [showErrorTrigger, setShowErrorTrigger] = useState(false)
  const [seatbeltUM, setSeatbeltUM] = useState(value.seatbelt || null)

  useEffect(() => {
    if (isKph) {
      value.seatbelt = Math.floor(seatbeltUM * MILES_TO_KPH);
      setSeatbeltUM(value.seatbelt)
    }
  }, [])

  useEffect(() => {
    if (value.dtc == undefined){
      onChange({
        ...value,
        dtc: false
      })
    }

    setTimeout(() => {
      setCurrentAlert({ ...currentAlert, conditions: { isJbus: true } })
    }, 1000)
  }, [value])

  /** VARS */
  const voltages = useMemo(() => {
    let values = []
    let voltage = 6
    while (voltage < 14) {
      values.push({
        id: voltage,
        name: `${voltage.toString().length > 2 ? voltage : `${voltage}.0`}`
      });
      voltage += 0.5
    }
    return values
  }, [])

  const rpms = useMemo(() => {
    let values = []
    let rpm = 500
    while (rpm < 10001) {
      values.push({
        id: rpm,
        name: `${rpm}`
      })
      rpm = rpm + 500
    }
    return values
  }, [])

  const speeds = useMemo(() => {
    let values = []
    let speed = 15
    while (speed < 151) {
      values.push({
        id: speed,
        name: `${speed}`
      })
      speed = speed + 1
    }
    return values
  }, [])

  const hazardOptions = useMemo(() => [
    { id: "Hazard Off", name: messages['alertsNewAlert_JBus_Off'] },
    { id: "Hazard Flashing", name: messages['alertsNewAlert_JBus_Flashing'] },
    { id: "Error", name: messages['alertsNewAlert_JBus_Error'] }
  ], [messages])

  const positions = useMemo(() => {
    let values = []
    let position = 5
    while (position < 101) {
      values.push({
        id: position,
        name: `${position}`
      })
      position = position + 5
    }
    return values
  }, [])

  let brakesOptions = useMemo(() => [
    { id: "Off", name: messages['alertsNewAlert_JBus_Off'] },
    { id: "On", name: messages['alertsNewAlert_JBus_On'] },
    { id: "Error", name: messages['alertsNewAlert_JBus_Error'] }
  ], [messages])

  let activeInactiveOptions = useMemo(() => [
    { id: "Active", name: messages['alertsNewAlert_JBus_Active'] },
    { id: "Inactive", name: messages['alertsNewAlert_JBus_Inactive'] }
  ], [messages])

  const fuelRates = useMemo(() => {
    let values = [];
    values.push({ id: 0.1, name: `0.1` });
    let fuelRate = 0.5
    while (fuelRate < 5.1) {
      values.push({
        id: fuelRate,
        name: `${`${fuelRate}`.length > 1 ? fuelRate : `${fuelRate}.0`}`
      })
      fuelRate = fuelRate + 0.5
    }
    return values
  }, [])

  const isValidRequiredItems = useCallback((clickNext = false) => {
    let isValid = false
    // validate all fields
    if (Object.values(value).some(v => (v != undefined && v !== false && v !== ''))) {
      isValid = true
    }
    if (value.trigger && clickNext) {
      setShowErrorTrigger(!isValid)
    }
    return isValid
  }, [value])

  return (
    <Form ref={formRef}
      onSubmit={() => {
        if (isKph) {
          value.seatbelt = seatbeltUM ? seatbeltUM / MILES_TO_KPH : null;
        }
        return isValidRequiredItems(true)
      }}
    >
      <Grid container justifyContent={'center'} direction={'column'} style={{ paddingTop: 15 }}>

        <div style={{ display: 'grid', paddingBottom: 10 }}>
          {
            (!isValidRequiredItems()) &&
            <small className="text-danger" style={{ marginTop: -5 }}>
              {messages["alertsNewAlert_BusValidation"]}
            </small>
          }
        </div>

        <div className="title_divider">{messages['alertsNewAlert_Bus_DiagnosticTroubleshootingCodes']}</div>

        { /* dtc */}
        <Grid item style={{ display: 'flex' }}>
          <ToggleSwitchWrapper
            id='dtc'
            label={messages['alertsNewAlert_JBus_Dtc']}
            checked={value.dtc == 1}
            onChange={(newValue) => onChange({ ...value, dtc: newValue })}
            xs={12} sm={12}
          />
        </Grid>

        <Grid style={{ padding: "10px 0" }} item>
          <Alert severity="warning">{messages['alertsNewAlert_Bus_NotAllFieldsAvailableToAlert']}</Alert>
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_General']}</div>

        { /* batteryVoltage */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_BatteryVoltage']}
            </label>
          </Grid>

          <SliderWrapper
            min={6}
            max={13.5}
            value={value.batteryVoltage || 0}
            onChange={(selected) => onChange({ ...value, batteryVoltage: selected })}
            sliderStep={0.5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="batteryVoltage"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={voltages}
            value={voltages.find((option) => option.id === value.batteryVoltage)}
            onChange={(selected) => onChange({ ...value, batteryVoltage: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* engineCrankCasePressure */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineCrankCasePressure']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.engineCrankCasePressure || 0}
            onChange={(selected) => onChange({ ...value, engineCrankCasePressure: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <InputWrapper
            id="engineCrankCasePressure"
            type="number"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            maxLength={3}
            min={0}
            max={100}
            onChange={(selected) => onChange({ ...value, engineCrankCasePressure: selected })}
            value={value.engineCrankCasePressure || ""}
            xs={12} sm={3}
          />
        </Grid>

        { /* rpm */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_Rpm']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={10000}
            value={value.rpm || 0}
            onChange={(selected) => onChange({ ...value, rpm: selected })}
            sliderStep={500}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="rpm"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={rpms}
            value={rpms.find((option) => option.id === value.rpm)}
            onChange={(selected) => onChange({ ...value, rpm: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* seatbelt */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_Seatbelt']?.replace('SPEED_UM', (isKph ? 'kph' : 'mph'))}
            </label>
          </Grid>

          <SliderWrapper
            min={15}
            max={150}
            value={value.seatbelt || 0}
            onChange={(selected) => { setSeatbeltUM(selected); onChange({ ...value, seatbelt: selected }) }}
            sliderStep={1}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="seatbelt"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={speeds}
            value={speeds.find((option) => option.id === value.seatbelt)}
            onChange={(selected) => { setSeatbeltUM(selected?.id); onChange({ ...value, seatbelt: selected?.id }) }}
            xs={12} sm={3}
          />
        </Grid>

        { /* hazard */}
        <Grid item>
          <SelectWrapper
            value={hazardOptions.find((option) => option.id === value.hazard)}
            onChange={(selected) => onChange({ ...value, hazard: selected?.id })}
            id="hazard"
            label={messages['alertsNewAlert_JBus_Hazard']}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={hazardOptions}
            xs={12} sm={12}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_Pedals']}</div>

        { /* acceleratorPedalPosition */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_AcceleratorPedalPosition']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.acceleratorPedalPosition || 0}
            onChange={(selected) => onChange({ ...value, acceleratorPedalPosition: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="acceleratorPedalPosition"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.acceleratorPedalPosition)}
            onChange={(selected) => onChange({ ...value, acceleratorPedalPosition: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* brakePedalPosition */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_BrakePedalPosition']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.brakePedalPosition || 0}
            onChange={(selected) => onChange({ ...value, brakePedalPosition: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="brakePedalPosition"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.brakePedalPosition)}
            onChange={(selected) => onChange({ ...value, brakePedalPosition: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_Brakes']}</div>

        { /* brakeSystemSwitchStatus */}
        <Grid item>
          <SelectWrapper
            value={brakesOptions.find((option) => option.id === value.brakeSystemSwitchStatus)}
            onChange={(selected) => onChange({ ...value, brakeSystemSwitchStatus: selected?.id })}
            id="brakeSystemSwitchStatus"
            label={messages['alertsNewAlert_JBus_BrakeSystemSwitchStatus']}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={brakesOptions}
            xs={12} sm={12}
          />
        </Grid>

        { /* serviceBrakeSwitchStatus */}
        <Grid item>
          <SelectWrapper
            value={brakesOptions.find((option) => option.id === value.serviceBrakeSwitchStatus)}
            onChange={(selected) => onChange({ ...value, serviceBrakeSwitchStatus: selected?.id })}
            id="serviceBrakeSwitchStatus"
            label={messages['alertsNewAlert_JBus_ServiceBrakeSwitchStatus']}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={brakesOptions}
            xs={12} sm={12}
          />
        </Grid>

        { /* parkingBrakeSwitch */}
        <Grid item>
          <SelectWrapper
            value={activeInactiveOptions.find((option) => option.id === value.parkingBrakeSwitch)}
            onChange={(selected) => onChange({ ...value, parkingBrakeSwitch: selected?.id })}
            id="parkingBrakeSwitch"
            label={messages['alertsNewAlert_JBus_ParkingBrakeSwitch']}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={activeInactiveOptions}
            xs={12} sm={12}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_Coolant']}</div>

        { /* engineCoolantLevel */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineCoolantLevel']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.engineCoolantLevel || 0}
            onChange={(selected) => onChange({ ...value, engineCoolantLevel: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="engineCoolantLevel"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.engineCoolantLevel)}
            onChange={(selected) => onChange({ ...value, engineCoolantLevel: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* engineCoolantPressure */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineCoolantPressure']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.engineCoolantPressure || 0}
            onChange={(selected) => onChange({ ...value, engineCoolantPressure: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <InputWrapper
            id="engineCoolantPressure"
            type="number"
            style={{ width: 'auto' }}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            maxLength={3}
            min={0}
            max={100}
            onChange={(selected) => onChange({ ...value, engineCoolantPressure: selected })}
            value={value.engineCoolantPressure || ""}
            xs={12} sm={3}
          />
        </Grid>

        { /* engineCoolantTemp */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineCoolantTemp']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={1000}
            value={value.engineCoolantTemp || 0}
            onChange={(selected) => onChange({ ...value, engineCoolantTemp: selected })}
            sliderStep={50}
            xs={12} sm={9}
          />

          <InputWrapper
            id="engineCoolantTemp"
            type="number"
            style={{ width: 'auto' }}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            maxLength={4}
            min={0}
            max={1000}
            onChange={(selected) => onChange({ ...value, engineCoolantTemp: selected })}
            value={value.engineCoolantTemp || ""}
            xs={12} sm={3}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_Oil']}</div>

        { /* engineOilLevel */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineOilLevel']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.engineOilLevel || 0}
            onChange={(selected) => onChange({ ...value, engineOilLevel: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="engineOilLevel"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.engineOilLevel)}
            onChange={(selected) => onChange({ ...value, engineOilLevel: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* engineOilPressure */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineOilPressure']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.engineOilPressure || 0}
            onChange={(selected) => onChange({ ...value, engineOilPressure: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <InputWrapper
            id="engineOilPressure"
            type="number"
            style={{ width: 'auto' }}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            maxLength={3}
            min={0}
            max={100}
            onChange={(selected) => onChange({ ...value, engineOilPressure: selected })}
            value={value.engineOilPressure || ""}
            xs={12} sm={3}
          />
        </Grid>

        { /* engineOilTemp */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineOilTemp']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={1000}
            value={value.engineOilTemp || 0}
            onChange={(selected) => onChange({ ...value, engineOilTemp: selected })}
            sliderStep={50}
            xs={12} sm={9}
          />

          <InputWrapper
            id="engineOilTemp"
            type="number"
            style={{ width: 'auto' }}
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            maxLength={4}
            min={0}
            max={1000}
            onChange={(selected) => onChange({ ...value, engineOilTemp: selected })}
            value={value.engineOilTemp || ""}
            xs={12} sm={3}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_LampIndicators']}</div>

        { /* redStopLampStatus - amberWarningLamp */}
        <Grid item style={{ display: 'flex' }}>
          <ToggleSwitchWrapper
            id='redStopLampStatus'
            label={messages['alertsNewAlert_JBus_RedStopLampStatus']}
            checked={value.redStopLampStatus == 1}
            onChange={(newValue) => onChange({ ...value, redStopLampStatus: newValue })}
            xs={6} sm={6}
          />

          <ToggleSwitchWrapper
            id='amberWarningLamp'
            label={messages['alertsNewAlert_JBus_AmberWarningLamp']}
            checked={value.amberWarningLamp == 1}
            onChange={(newValue) => onChange({ ...value, amberWarningLamp: newValue })}
            xs={6} sm={6}
          />
        </Grid>

        { /*  protectLampStatus - flashMalfunctionIndicatorLamp */}
        <Grid item style={{ display: 'flex' }}>
          <ToggleSwitchWrapper
            id='protectLampStatus'
            label={messages['alertsNewAlert_JBus_ProtectLampStatus']}
            checked={value.protectLampStatus == 1}
            onChange={(newValue) => onChange({ ...value, protectLampStatus: newValue })}
            xs={6} sm={6}
          />

          <ToggleSwitchWrapper
            id='flashMalfunctionIndicatorLamp'
            label={messages['alertsNewAlert_JBus_FlashMalfunctionIndicatorLamp']}
            checked={value.flashMalfunctionIndicatorLamp == 1}
            onChange={(newValue) => onChange({ ...value, flashMalfunctionIndicatorLamp: newValue })}
            xs={6} sm={6}
          />
        </Grid>

        { /* flashRedStopLamp - flashAmberWarningLamp */}
        <Grid item style={{ display: 'flex' }}>
          <ToggleSwitchWrapper
            id='flashRedStopLamp'
            label={messages['alertsNewAlert_JBus_FlashRedStopLamp']}
            checked={value.flashRedStopLamp == 1}
            onChange={(newValue) => onChange({ ...value, flashRedStopLamp: newValue })}
            xs={6} sm={6}
          />

          <ToggleSwitchWrapper
            id='flashAmberWarningLamp'
            label={messages['alertsNewAlert_JBus_FlashAmberWarningLamp']}
            checked={value.flashAmberWarningLamp == 1}
            onChange={(newValue) => onChange({ ...value, flashAmberWarningLamp: newValue })}
            xs={6} sm={6}
          />
        </Grid>

        { /* flashProtectLamp */}
        <Grid item style={{ display: 'flex' }}>
          <ToggleSwitchWrapper
            id='flashProtectLamp'
            label={messages['alertsNewAlert_JBus_FlashProtectLamp']}
            checked={value.flashProtectLamp == 1}
            onChange={(newValue) => onChange({ ...value, flashProtectLamp: newValue })}
            xs={12} sm={12}
          />
        </Grid>

        <div className="title_divider">{messages['alertsNewAlert_JBus_Fuel']}</div>

        { /* engineFuelRate */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_EngineFuelRate']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={5}
            value={value.engineFuelRate || 0}
            onChange={(selected) => onChange({ ...value, engineFuelRate: selected })}
            sliderStep={0.5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="engineFuelRate"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={fuelRates}
            value={fuelRates.find((option) => option.id === value.engineFuelRate)}
            onChange={(selected) => onChange({ ...value, engineFuelRate: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* fuelLevelOne */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_FuelLevelOne']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.fuelLevelOne || 0}
            onChange={(selected) => onChange({ ...value, fuelLevelOne: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="fuelLevelOne"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.fuelLevelOne)}
            onChange={(selected) => onChange({ ...value, fuelLevelOne: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

        { /* fuelLevelTwo */}
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          style={{ width: '100%' }}
        >
          <Grid item xs={12} sm={12} style={{ paddingTop: 10 }}>
            <label>
              {messages['alertsNewAlert_JBus_FuelLevelTwo']}
            </label>
          </Grid>

          <SliderWrapper
            min={0}
            max={100}
            value={value.fuelLevelTwo || 0}
            onChange={(selected) => onChange({ ...value, fuelLevelTwo: selected })}
            sliderStep={5}
            xs={12} sm={9}
          />

          <SelectWrapper
            id="fuelLevelTwo"
            placeholder={messages['alertsNewAlert_Bus_NotSet']}
            options={positions}
            value={positions.find((option) => option.id === value.fuelLevelTwo)}
            onChange={(selected) => onChange({ ...value, fuelLevelTwo: selected?.id })}
            xs={12} sm={3}
          />
        </Grid>

      </Grid>
    </Form >
  );
}

/** WRAPPERS */
function ToggleSwitchWrapper({ id, label, checked, onChange, xs, sm }) {
  return <Grid item xs={xs} sm={sm}>
    <ToggleSwitch
      id={id}
      small
      blueToggle
      optionLabels={["", ""]}
      label={label}
      checked={checked}
      onChange={onChange}
    />
  </Grid>
}

function SliderWrapper({ min, max, value, onChange, sliderStep, xs, sm }) {
  return <Grid item xs={xs} sm={sm} style={{ paddingRight: 25 }}>
    <Slider
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      sliderOneColor={false}
      valueLabelDisplay={'off'}
      sliderStep={sliderStep}
    />
  </Grid>
}

function InputWrapper({ id, type, placeholder, maxLength, min, max, value, onChange, xs, sm }) {
  return <Grid item xs={xs} sm={sm}>
    <Input
      id={id}
      type={type}
      style={{ width: 'auto' }}
      placeholder={placeholder}
      containerStyle={{ paddingTop: 0 }}
      maxLength={maxLength}
      min={min}
      max={max}
      onlyPositive
      onChange={onChange}
      value={value}
    />
  </Grid>
}

function SelectWrapper({ id, label, placeholder, options, value, onChange, xs, sm }) {
  return <Grid item xs={xs} sm={sm}>
    <Select
      id={id}
      value={value}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      options={options}
      textField="name"
      valueField="id"
      isClearable
      containerStyle={{ paddingTop: 0 }}
    />
  </Grid>
}